import React from 'react';
import { Box, Paper } from '@mui/material';
import { CircularLoader } from '../CircularLoader';
import { NoDataFoundWrapper, NoResultsText } from '../../Containers/Search/styles';
import {
  HeadingIcon,
  KBChips,
  KBTags,
  KBTagsStack,
  TagHeading,
} from '../SearchBox/styles';
import {
  EXTRA_TECH_TAGS,
  STATIC_RESOURCE_TAGS,
  TagClickType,
} from './constants';

interface TagItem {
  id?: number | string;
  attributes: { name: string; query?: string };
}

interface TagRowProps {
  tags?: TagItem[];
  title: string;
  icon: React.ReactNode;
  variant: 'filled' | 'outlined';
  loading?: boolean;
  hasError?: boolean;
  type: TagClickType;
  extraTags?: string[];
  selectedTags?: string[];
  onTagClick: (tagName: string, type: TagClickType, resourceQuery?: string) => void;
}

const isTagSelected = (name: string, selectedTags?: string[]) =>
  selectedTags?.some((t) => t.toLowerCase() === name.toLowerCase()) ?? false;

const TagRow: React.FC<TagRowProps> = ({
  tags,
  title,
  icon,
  variant,
  loading,
  hasError,
  type,
  extraTags,
  selectedTags,
  onTagClick,
}) => (
  <KBTags>
    {loading ? (
      <Box flexGrow={1} display="flex" alignItems="center">
        <CircularLoader />
      </Box>
    ) : !hasError ? (
      <>
        <Box>
          <TagHeading icon={icon} title={title} />
        </Box>
        <KBTagsStack direction="row" spacing={1}>
          {type !== 'resources' &&
            tags?.map((tag) => {
              const name = tag.attributes.name;
              const selected = isTagSelected(name, selectedTags);
              return (
                <KBChips
                  key={tag.id ?? name}
                  variant={selected ? 'filled' : variant}
                  color={selected ? 'primary' : 'default'}
                  onClick={() => onTagClick(name, type)}
                  label={name}
                  clickable
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={`${selected ? 'Deselect' : 'Select'} ${name}`}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTagClick(name, type);
                    }
                  }}
                />
              );
            })}
          {extraTags?.map((label) => {
            const selected = isTagSelected(label, selectedTags);
            return (
              <KBChips
                key={label}
                variant={selected ? 'filled' : 'outlined'}
                color={selected ? 'primary' : 'default'}
                onClick={() => onTagClick(label, type)}
                label={label}
                clickable
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                aria-label={`${selected ? 'Deselect' : 'Select'} ${label}`}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onTagClick(label, type);
                  }
                }}
              />
            );
          })}
          {type === 'resources' &&
            tags?.map((tag, index) => {
              const name = tag.attributes.name;
              const selected = isTagSelected(name, selectedTags);
              return (
                <KBChips
                  key={tag.attributes.query ?? index}
                  variant={selected ? 'filled' : variant}
                  color={selected ? 'primary' : 'default'}
                  onClick={() => onTagClick(name, type, tag.attributes.query)}
                  label={name}
                  clickable
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={`${selected ? 'Deselect' : 'Select'} ${name}`}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTagClick(name, type, tag.attributes.query);
                    }
                  }}
                />
              );
            })}
        </KBTagsStack>
      </>
    ) : (
      <NoDataFoundWrapper>
        <NoResultsText title="No results found." />
      </NoDataFoundWrapper>
    )}
  </KBTags>
);

export interface TagSectionsProps {
  businessTags?: TagItem[];
  techTags?: TagItem[];
  isBusinessTagsLoading?: boolean;
  isTechTagsLoading?: boolean;
  businessTagsError?: boolean;
  techTagsError?: boolean;
  showResources?: boolean;
  selectedTags?: string[];
  /** dropdown = under landing search; sidebar = documents page right column */
  layout?: 'dropdown' | 'sidebar';
  onTagClick: (tagName: string, type: TagClickType, resourceQuery?: string) => void;
}

const TagSections: React.FC<TagSectionsProps> = ({
  businessTags,
  techTags,
  isBusinessTagsLoading,
  isTechTagsLoading,
  businessTagsError,
  techTagsError,
  showResources = true,
  selectedTags,
  layout = 'dropdown',
  onTagClick,
}) => {
  const rowProps = { selectedTags, onTagClick };

  const content = (
    <>
      <TagRow
        tags={businessTags}
        variant="filled"
        icon={<HeadingIcon width={17} height={18} kind="industry" />}
        title="Industry"
        loading={isBusinessTagsLoading}
        type="business_tags"
        hasError={businessTagsError}
        {...rowProps}
      />
      <TagRow
        tags={techTags}
        variant="outlined"
        icon={<HeadingIcon width={15} height={15} kind="technology" />}
        title="Technology"
        loading={isTechTagsLoading}
        type="tech_tags"
        hasError={techTagsError}
        extraTags={EXTRA_TECH_TAGS}
        {...rowProps}
      />
      {showResources && (
        <TagRow
          tags={STATIC_RESOURCE_TAGS}
          variant="filled"
          icon={<HeadingIcon width={17} height={20} kind="logo-icon" />}
          title="Talentica Resources"
          loading={false}
          type="resources"
          hasError={false}
          {...rowProps}
        />
      )}
    </>
  );

  if (layout === 'sidebar') {
    return (
      <Paper
        variant="outlined"
        sx={{
          borderColor: '#DBDBDB',
          borderRadius: '0.25rem',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 8rem)',
          overflowY: 'auto',
          '& .MuiBox-root': {
            maxHeight: 'none',
          },
        }}
        data-testid="docs-tag-sections"
      >
        {content}
      </Paper>
    );
  }

  return <>{content}</>;
};

export default TagSections;
